import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";

import api from "src/api/rest";
import { firebaseAuth } from "#app/firebase";
import { useAppDispatch } from "src/redux/hooks";
import { clearUser, setUser } from "src/redux/slices";
import { clearPersistedActiveStoreId } from "#hooks/initial/use-persist-store-id";

export type FirebaseAuthBootstrap =
    | { status: "loading" }
    | { status: "signedOut" }
    | { status: "ready" }
    | { status: "error"; error: Error };

// signinPreload speeds up loading by ~250ms
// so we fetch twice. one at preload and one here
export function useFirebaseAuth(): FirebaseAuthBootstrap {
    const dispatch = useAppDispatch();
    const [authBootstrap, setAuthBootstrap] = useState<FirebaseAuthBootstrap>({
        status: "loading",
    });

    useEffect(() => {
        let active = true;
        let authGeneration = 0;
        const unsubscribe = onAuthStateChanged(
            firebaseAuth,
            async (updatedUser) => {
                if (!active) return;

                const generation = ++authGeneration;
                const isCurrentGeneration = () =>
                    active && generation === authGeneration;
                setAuthBootstrap({ status: "loading" });
                if (!updatedUser) {
                    dispatch(clearUser());
                    clearPersistedActiveStoreId();
                    setAuthBootstrap({ status: "signedOut" });
                    return;
                }
                try {
                    // This is needed because the user token on the axios
                    // client doesn't get applied quick enough
                    const idToken = await updatedUser?.getIdToken();
                    if (!isCurrentGeneration()) return;

                    const response = await api.users.getMe(idToken).catch(
                        async (error) => {
                            // A token that expired in flight is not an account problem:
                            // refresh it and try once more before showing the error page.
                            if (!api.users.isExpiredTokenError(error)) throw error;
                            const fresh = await updatedUser.getIdToken(true);
                            return api.users.getMe(fresh);
                        },
                    );
                    if (!isCurrentGeneration()) return;

                    const { user, hash } = response.data;
                    dispatch(setUser({ ...user, intercomHash: hash }));
                    setAuthBootstrap({ status: "ready" });
                } catch (e) {
                    if (!isCurrentGeneration()) return;

                    console.log("Error fetching user: ", e);
                    dispatch(clearUser());
                    clearPersistedActiveStoreId();
                    setAuthBootstrap({
                        status: "error",
                        error:
                            e instanceof Error
                                ? e
                                : new Error(
                                      "Failed to load Snackpass identity",
                                  ),
                    });
                }
            },
        );
        return () => {
            active = false;
            authGeneration += 1;
            unsubscribe();
        };
    }, [dispatch]);

    return authBootstrap;
}
